import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/react'
import CardGiftcardOutlinedIcon from '@mui/icons-material/CardGiftcardOutlined'
import CheckIcon from '@mui/icons-material/Check'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CloseIcon from '@mui/icons-material/Close'
import EmojiObjectsOutlinedIcon from '@mui/icons-material/EmojiObjectsOutlined'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined'
import RemoveIcon from '@mui/icons-material/Remove'
import SwipeableDrawer from '@mui/material/SwipeableDrawer'
import { useId, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Text } from '../../../components/design-system/primitives/Typography/Text'
import CheckCircleOn from '../../../images/design-system/ic-check-circle-on.svg?react'
import PremiumIconWhite from '../assets/landing/premium-icon-white.svg?react'
import { landingAssetsForLocale } from '../config/landingAssets'
import { PurchaseChannelConfig } from '../types'
import { buildPurchaseChannelDisplayStates } from '../utils/purchaseButtonState'

type ComparisonCell =
  | { type: 'text'; value: string; premium?: boolean }
  | { type: 'check'; premium?: boolean }
  | { type: 'minus' }
  | { type: 'list'; values: string[] }

interface ComparisonRow {
  label: string
  free: ComparisonCell
  premium: ComparisonCell
}

interface ProductPricingSectionProps {
  isPremiumUser: boolean | null
  premiumDeepLinkUrl: string
  purchaseChannels: PurchaseChannelConfig[]
  onAmazonClick: () => void
  logClickedLearnMoreAboutPremium: () => void
}

const PLAN_STYLES = {
  one: {
    background: 'bg-mi-premium-user',
    cellBackground: 'bg-[#E6F3FF]',
    checkFill: 'fill-mi-premium-user',
  },
  family: {
    background: 'bg-mi-bg-secondary',
    cellBackground: 'bg-[#FEEAED]',
    checkFill: 'fill-mi-bg-secondary',
  },
  familyPro: {
    background: 'bg-[#C3AB62]',
    cellBackground: 'bg-[#FFF1DB]',
    checkFill: 'fill-[#C3AB62]',
  },
} as const
const PREMIUM_PLANS = ['one', 'family', 'familyPro'] as const

function PinkBenefitList({ items }: { items: string[] }) {
  return (
    <ul className='space-y-3'>
      {items.map(item => (
        <li key={item} className='flex items-center gap-2 text-base leading-[1.6] text-mi-text-dark'>
          <CheckIcon
            data-testid='premium-benefit-check'
            className='h-3.5 w-3.5 flex-none text-mi-bg-secondary'
          />
          <span className='whitespace-pre-line'>{item}</span>
        </li>
      ))}
    </ul>
  )
}

function PlanBadge({ color, children }: { color: string; children: React.ReactNode }) {
  return (
    <span className={`${color} rounded px-2 py-1 text-s font-bold leading-[18px] text-white`}>
      {children}
    </span>
  )
}

function PlanCheck({ fill }: { fill: string }) {
  return (
    <CheckCircleOn
      data-testid='premium-plan-check'
      className={`mx-auto h-4 w-4 ${fill}`}
    />
  )
}

/** 金額と単位を分け、単位は通常ウェイトで表示する。 */
function PlanPrice({ value }: { value: string }) {
  const [amount, unit] = value.split('\n')

  return (
    <>
      {amount}
      {unit && (
        <>
          <br />
          <span className='font-normal'>{unit}</span>
        </>
      )}
    </>
  )
}

function ComparisonCellContent({ cell, isEnglish }: { cell: ComparisonCell; isEnglish: boolean }) {
  if (cell.type === 'text') {
    return (
      <span className={`whitespace-pre-line ${cell.premium ? 'text-mi-bg-secondary' : ''}`}>
        {cell.value}
      </span>
    )
  }

  if (cell.type === 'minus') {
    return <RemoveIcon className='h-4 w-4' />
  }

  if (cell.type === 'list') {
    return (
      <ul className='text-left text-mi-bg-secondary'>
        {cell.values.map(value => (
          <li key={value}>・{value}</li>
        ))}
      </ul>
    )
  }

  if (isEnglish) {
    return (
      <CheckCircleIcon
        className={`h-5 w-5 ${
          cell.premium ? 'text-mi-bg-secondary' : 'text-mi-text-gray'
        }`}
      />
    )
  }

  return <CheckIcon className={`h-4 w-4 ${cell.premium ? 'text-mi-bg-secondary' : ''}`} />
}

function ComparisonTable({ isEnglish, rows }: { isEnglish: boolean; rows: ComparisonRow[] }) {
  const { t } = useTranslation('landingPage')

  return (
    <div data-testid='free-premium-comparison-table' className='w-full overflow-hidden rounded-lg'>
      <div className='grid grid-cols-[104px_1fr_1fr] gap-px text-center'>
        <div />
        <div className='flex min-h-[58px] items-center justify-center rounded-tl-lg bg-mi-text-dark-gray px-2 py-3 text-sm font-semibold text-white'>
          {t('pricing.comparison.freeColumn')}
        </div>
        <div className='flex min-h-[58px] flex-col items-center justify-center rounded-tr-lg bg-mi-on-surface-accent-secondary px-2 py-3 text-s font-semibold text-white'>
          <span className='flex items-center gap-1'>
            <PremiumIconWhite className='h-5 w-5' />
            {t('pricing.comparison.premiumColumn')}
          </span>
          <span className='leading-4'>
            {PREMIUM_PLANS.map(plan => t(`premiumPlans.plans.${plan}.comparisonName`)).join('/')}
          </span>
        </div>
        {rows.map((row, index) => (
          <div key={row.label} className='contents'>
            <div
              className={`flex min-h-12 items-center bg-white px-2 py-3 text-left text-xs leading-[1.35] ${
                index === 0 ? 'rounded-tl-lg' : ''
              } ${
                index === rows.length - 1 ? 'rounded-bl-lg' : ''
              }`}
            >
              {row.label}
            </div>
            <div className='flex min-h-12 items-center justify-center bg-white px-1 py-3 text-center text-s font-medium leading-[1.35]'>
              <ComparisonCellContent cell={row.free} isEnglish={isEnglish} />
            </div>
            <div
              className={`flex min-h-12 items-center justify-center bg-white px-1 py-3 text-center text-s font-medium leading-[1.35] ${
                index === rows.length - 1 ? 'rounded-br-lg' : ''
              }`}
            >
              <ComparisonCellContent cell={row.premium} isEnglish={isEnglish} />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function PlanComparisonTable() {
  const { t } = useTranslation('landingPage')

  return (
    <table
      data-testid='premium-plan-comparison-table'
      className='w-full table-fixed border-separate border-spacing-0 text-xs text-mi-text-dark'
    >
      <colgroup>
        <col className='w-[124px]' />
        {PREMIUM_PLANS.map(plan => (
          <col key={plan} />
        ))}
      </colgroup>
      <thead>
        <tr>
          <th aria-hidden='true' />
          {PREMIUM_PLANS.map((plan, index) => (
            <th
              key={plan}
              scope='col'
              className={`${PLAN_STYLES[plan].background} h-[62px] border-l border-t border-mi-border px-1 py-2 text-center font-bold leading-4 text-white ${
                index === 0 ? 'rounded-tl-lg' : ''
              } ${index === PREMIUM_PLANS.length - 1 ? 'rounded-tr-lg border-r' : ''}`}
            >
              {t(`premiumPlans.plans.${plan}.name`)}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        <tr>
          <th
            scope='row'
            className='h-12 rounded-tl-lg border-l border-t border-mi-border bg-white px-2 py-3 text-left font-normal'
          >
            {t('premiumPlans.table.price')}
          </th>
          {PREMIUM_PLANS.map((plan, index) => (
            <td
              key={`${plan}-price`}
              className={`${PLAN_STYLES[plan].cellBackground} h-12 whitespace-pre-line border-l border-t border-mi-border px-1 py-3 text-center font-bold ${
                index === PREMIUM_PLANS.length - 1 ? 'border-r' : ''
              }`}
            >
              <PlanPrice value={t(`premiumPlans.plans.${plan}.price`)} />
            </td>
          ))}
        </tr>
        <tr>
          <th
            scope='row'
            className='h-12 border-l border-t border-mi-border bg-white px-2 py-3 text-left font-normal leading-4'
          >
            {t('premiumPlans.table.frameFeatures')}
          </th>
          {PREMIUM_PLANS.map((plan, index) => (
            <td
              key={`${plan}-features`}
              className={`${PLAN_STYLES[plan].cellBackground} h-12 border-l border-t border-mi-border px-1 py-3 text-center ${
                index === PREMIUM_PLANS.length - 1 ? 'border-r' : ''
              }`}
            >
              <PlanCheck fill={PLAN_STYLES[plan].checkFill} />
            </td>
          ))}
        </tr>
        <tr>
          <th
            scope='row'
            className='h-[60px] rounded-bl-lg border-y border-l border-mi-border bg-white px-2 py-3 text-left font-normal leading-4'
          >
            {t('premiumPlans.table.shareBenefits')}
          </th>
          {PREMIUM_PLANS.map((plan, index) => (
            <td
              key={`${plan}-sharing`}
              className={`${PLAN_STYLES[plan].cellBackground} h-[60px] border-b border-l border-t border-mi-border px-1 py-3 text-center ${
                index === PREMIUM_PLANS.length - 1 ? 'rounded-br-lg border-r' : ''
              }`}
            >
              <span className='inline-flex items-center justify-center gap-0.5'>
                {index === 0 ? (
                  <RemoveIcon className='h-4 w-4' />
                ) : (
                  <>
                    <PlanCheck fill={PLAN_STYLES[plan].checkFill} />
                    <span>*</span>
                  </>
                )}
              </span>
            </td>
          ))}
        </tr>
      </tbody>
    </table>
  )
}

function PremiumPlansModal({
  isOpen,
  onClose,
  premiumDeepLinkUrl,
  logClickedLearnMoreAboutPremium,
}: {
  isOpen: boolean
  onClose: () => void
  premiumDeepLinkUrl: string
  logClickedLearnMoreAboutPremium: () => void
}) {
  const { t } = useTranslation('landingPage')
  const headingId = useId()
  const faqItems = [0, 1, 2]

  return (
    <SwipeableDrawer
      anchor='bottom'
      open={isOpen}
      onClose={onClose}
      onOpen={() => undefined}
      disableSwipeToOpen={true}
      disableDiscovery={true}
      hysteresis={0.35}
      minFlingVelocity={400}
      transitionDuration={{ enter: 300, exit: 250 }}
      slotProps={{
        backdrop: {
          sx: { backgroundColor: 'rgba(0, 0, 0, 0.6)' },
        },
        paper: {
          'aria-labelledby': headingId,
          className: 'flex flex-col overflow-hidden bg-white text-mi-text-dark',
          sx: {
            height: '80dvh',
            width: '100%',
            maxWidth: '430px',
            mx: 'auto',
            borderRadius: '16px 16px 0 0',
          },
        },
      }}
    >
      <div className='overflow-y-auto px-4 pt-2'>
        <div className='flex justify-center pb-1'>
          <div className='h-1 w-10 rounded-full bg-mi-text-quaternary' aria-hidden='true' />
        </div>
        <div className='mb-4 flex justify-end px-2'>
          <button
            type='button'
            aria-label={t('premiumPlans.close')}
            className='flex h-8 w-8 items-center justify-center'
            onClick={onClose}
          >
            <CloseIcon className='h-6 w-6' />
          </button>
        </div>

        <div className='space-y-2'>
          <h2 id={headingId} className='whitespace-pre-line text-xl font-bold leading-[1.4]'>
            {t('premiumPlans.heading')}
          </h2>
          <p className='text-base leading-[1.6]'>
            {t('premiumPlans.introBefore')}
            <strong>{t('premiumPlans.introEmphasis')}</strong>
            {t('premiumPlans.introAfter')}
          </p>
        </div>

        <div className='mt-4'>
          <PlanComparisonTable />
        </div>
        <p className='mt-3 text-s leading-[1.4]'>{t('premiumPlans.table.note')}</p>

        <Disclosure>
          {({ open }) => (
            <div className='mt-4 border-y border-mi-text-quaternary'>
              <DisclosureButton className='flex w-full items-center gap-1 py-4 text-left'>
                <EmojiObjectsOutlinedIcon className='h-5 w-5' />
                <span className='flex-1 text-base font-bold'>
                  {t('premiumPlans.differences.heading')}
                </span>
                {open ? (
                  <ExpandLessIcon className='h-6 w-6 text-mi-text-gray' />
                ) : (
                  <ExpandMoreIcon className='h-6 w-6 text-mi-text-gray' />
                )}
              </DisclosureButton>
              <DisclosurePanel className='space-y-5 border-t border-mi-text-quaternary px-2 py-5 leading-[1.6]'>
                {PREMIUM_PLANS.map(plan => (
                  <div key={plan}>
                    <PlanBadge color={PLAN_STYLES[plan].background}>
                      {t(`premiumPlans.plans.${plan}.name`)}
                    </PlanBadge>
                    <p className='mt-2 whitespace-pre-line text-base font-normal'>
                      {t(`premiumPlans.differences.items.${plan}`)}
                    </p>
                  </div>
                ))}
                <Text size='small' component='p'>
                  {t('premiumPlans.differences.note')}
                </Text>
              </DisclosurePanel>
            </div>
          )}
        </Disclosure>

        <div className='space-y-2 pb-4 pt-7'>
          <h3 className='text-center text-xl font-bold leading-[1.4]'>
            {t('premiumPlans.faq.heading')}
          </h3>
          {faqItems.map(index => (
            <Disclosure key={index}>
              {({ open }) => (
                <div className='rounded-lg bg-mi-bg-light-gray'>
                  <DisclosureButton className='flex w-full items-center gap-2 px-6 py-4 text-left'>
                    <span className='flex-1 whitespace-pre-line text-base leading-[1.6]'>
                      {t(`premiumPlans.faq.items.${index}.question`)}
                    </span>
                    {open ? (
                      <ExpandLessIcon className='h-6 w-6 flex-none text-mi-text-gray' />
                    ) : (
                      <ExpandMoreIcon className='h-6 w-6 flex-none text-mi-text-gray' />
                    )}
                  </DisclosureButton>
                  <DisclosurePanel className='px-6 pb-4'>
                    <Text size='small' component='p' className='whitespace-pre-line'>
                      {t(`premiumPlans.faq.items.${index}.answer`)}
                    </Text>
                  </DisclosurePanel>
                </div>
              )}
            </Disclosure>
          ))}
        </div>
      </div>

      <div className='border-t border-mi-border bg-white px-4 pb-[max(12px,env(safe-area-inset-bottom))] pt-3'>
        <a
          href={premiumDeepLinkUrl}
          className='flex min-h-12 w-full items-center justify-center rounded-lg bg-mi-bg-primary px-4 text-center text-base font-bold text-white'
          onClick={logClickedLearnMoreAboutPremium}
        >
          {t('premiumPlans.cta')}
        </a>
      </div>
    </SwipeableDrawer>
  )
}

export default function ProductPricingSection({
  isPremiumUser,
  premiumDeepLinkUrl,
  purchaseChannels,
  onAmazonClick,
  logClickedLearnMoreAboutPremium,
}: ProductPricingSectionProps) {
  const { t, i18n } = useTranslation('landingPage')
  const assets = landingAssetsForLocale(i18n.language)
  const isEnglish = i18n.language.startsWith('en')
  const [isPremiumPlansModalOpen, setIsPremiumPlansModalOpen] = useState(false)
  const amazonDisplayState = useMemo(
    () =>
      buildPurchaseChannelDisplayStates(purchaseChannels).find(
        channel => channel.channel === 'amazon',
      ),
    [purchaseChannels],
  )
  const isAmazonAvailable =
    purchaseChannels.find(channel => channel.key === 'amazon')?.available === true

  const comparisonRows = useMemo<ComparisonRow[]>(() => {
    const commonRows: ComparisonRow[] = [
      {
        label: t('pricing.comparison.rows.price.name'),
        free: { type: 'text', value: t('pricing.comparison.rows.price.free') },
        premium: {
          type: 'text',
          value: t('pricing.comparison.rows.price.premium'),
          premium: true,
        },
      },
      {
        label: t('pricing.comparison.rows.mediaCount.name'),
        free: { type: 'text', value: t('pricing.comparison.rows.mediaCount.free') },
        premium: {
          type: 'text',
          value: t('pricing.comparison.rows.mediaCount.premium'),
          premium: true,
        },
      },
    ]

    if (!isEnglish) {
      commonRows.push({
        label: t('pricing.comparison.rows.multipleAlbums.name'),
        free: { type: 'check' },
        premium: { type: 'check', premium: true },
      })
    }

    return [
      ...commonRows,
      {
        label: t('pricing.comparison.rows.remoteSettings.name'),
        free: { type: 'check' },
        premium: { type: 'check', premium: true },
      },
      {
        label: t('pricing.comparison.rows.video.name'),
        free: { type: 'minus' },
        premium: { type: 'check', premium: true },
      },
      {
        label: t('pricing.comparison.rows.filtering.name'),
        free: { type: 'minus' },
        premium: {
          type: 'list',
          values: t('pricing.comparison.rows.filtering.premium').split('\n'),
        },
      },
    ]
  }, [isEnglish, t])

  const premiumBenefits = [
    t('pricing.premiumUpsell.benefits.0'),
    t('pricing.premiumUpsell.benefits.1'),
    t('pricing.premiumUpsell.benefits.2'),
  ]

  const openPremiumPlansModal = () => {
    logClickedLearnMoreAboutPremium()
    setIsPremiumPlansModalOpen(true)
  }

  return (
    <>
      <section className='space-y-6 bg-mi-bg-light-gray px-4 py-10 text-mi-text-dark'>
        <div className='mx-auto max-w-[358px] text-center'>
          <h2 className='text-2xl font-bold leading-[1.4]'>{t('pricing.heading')}</h2>
          {isEnglish && <p className='mt-2 text-base leading-[1.4]'>{t('pricing.intro')}</p>}
        </div>

        {!isEnglish && isPremiumUser && (
          <div className='mx-auto max-w-[358px] whitespace-pre-line rounded-lg bg-mi-bg-secondary px-4 py-2 text-center text-sm font-semibold leading-[22px] text-white'>
            {t('pricing.premiumUser.badge')}
          </div>
        )}

        {!isEnglish && isPremiumUser === false && (
          <div className='mx-auto max-w-[343px] text-base leading-[1.6]'>
            <p>{t('pricing.freeUser.bodyLead')}</p>
            <p>{t('pricing.freeUser.bodySub')}</p>
          </div>
        )}

        <div
          className={`mx-auto max-w-[358px] overflow-hidden bg-white ${
            isEnglish ? 'rounded-2xl shadow-[0_10px_16px_rgba(0,0,0,0.16)]' : 'rounded-lg'
          }`}
        >
          <div className={`${isEnglish ? 'px-4 pb-8 pt-12' : 'p-4'} space-y-6`}>
            <img
              src={assets.priceImg1}
              alt={t('pricing.product.imageAlt')}
              className={`mx-auto rounded-lg object-cover ${isEnglish ? 'max-w-[215px]' : 'w-full'}`}
              loading='lazy'
            />
            <div className='space-y-4'>
              <div>
                {isEnglish && (
                  <p className='text-s font-bold leading-[1.6]'>
                    {t('pricing.product.brand')}
                  </p>
                )}
                <h3 className={`${isEnglish ? 'text-2xl' : 'text-base'} font-bold leading-[1.4]`}>
                  {t('pricing.product.title')}
                </h3>
                <p className='mt-1 text-xl font-bold leading-[1.4] text-mi-bg-secondary'>
                  {t('pricing.product.price')}
                </p>
              </div>
              <p className='text-base leading-[1.6]'>
                <strong className='block font-bold'>{t('pricing.product.packageLabel')}</strong>
                {t('pricing.product.packageContent')}
              </p>
              {isEnglish && (
                <a
                  href={amazonDisplayState?.href ?? t('purchaseButtons.amazonUrl')}
                  target='_blank'
                  rel='noreferrer noopener'
                  className='flex min-h-12 w-full flex-col items-center justify-center rounded-lg bg-mi-surface-inverse px-4 text-center text-white'
                  onClick={onAmazonClick}
                >
                  <span className='text-base font-bold'>{t('pricing.product.amazonButton')}</span>
                  {!isAmazonAvailable && amazonDisplayState?.subText && (
                    <span className='mt-0.5 text-xs'>{amazonDisplayState.subText}</span>
                  )}
                </a>
              )}
            </div>
          </div>

          {isEnglish && isPremiumUser && (
            <div className='space-y-3 border-t border-dashed border-mi-text-gray px-4 pb-8 pt-6 text-center'>
              <div className='space-y-2 font-bold text-mi-bg-secondary'>
                <LockOpenOutlinedIcon className='mx-auto h-6 w-6' />
                <p>{t('pricing.premiumUser.badge')}</p>
              </div>
              <p className='text-base leading-[1.6]'>{t('pricing.premiumUser.bodyLead')}</p>
            </div>
          )}
        </div>

        {!isPremiumUser && (
          <div className='mx-auto max-w-[358px] space-y-6'>
            <div className='text-center text-[34px] font-bold leading-none text-mi-on-surface-accent-secondary'>
              +
            </div>
            {!isEnglish && (
              <h3 className='whitespace-pre-line text-center text-xl font-bold leading-[1.4] text-mi-on-surface-accent-secondary'>
                {t('pricing.premiumUpsell.heading')}
              </h3>
            )}
            <div
              className={`space-y-6 bg-white px-4 py-8 ${isEnglish ? 'rounded-2xl' : 'rounded-lg'}`}
            >
              <div className='space-y-2'>
                <h3 className='whitespace-pre-line text-2xl font-bold leading-[1.4]'>
                  {t('pricing.premiumUpsell.title')}
                </h3>
                <p className='text-base leading-[1.6]'>{t('pricing.premiumUpsell.price')}</p>
                <div className='flex gap-2'>
                  {PREMIUM_PLANS.map(plan => (
                    <PlanBadge key={plan} color={PLAN_STYLES[plan].background}>
                      {t(`premiumPlans.plans.${plan}.name`)}
                    </PlanBadge>
                  ))}
                </div>
              </div>
              <PinkBenefitList items={premiumBenefits} />
              {isEnglish && (
                <p className='text-base leading-[1.6]'>{t('pricing.premiumUpsell.bodySub')}</p>
              )}
              <a
                href={premiumDeepLinkUrl}
                className='mx-auto flex min-h-9 w-fit items-center justify-center rounded-full bg-mi-bg-light-gray px-4 text-center text-sm font-bold text-mi-text-primary'
                onClick={logClickedLearnMoreAboutPremium}
              >
                {t('pricing.premiumUpsell.button')}
              </a>
            </div>
          </div>
        )}

        <div className='mx-auto max-w-[358px] space-y-6'>
          <h3 className='text-center text-xl font-bold leading-[1.4]'>
            {t('pricing.comparison.heading')}
          </h3>
          <ComparisonTable isEnglish={isEnglish} rows={comparisonRows} />
          <button
            type='button'
            className='text-left text-base font-bold leading-[22px] text-mi-bg-primary'
            onClick={openPremiumPlansModal}
          >
            {t('pricing.comparison.learnMore')}
          </button>

          {isEnglish && (
            <Disclosure defaultOpen={true}>
              {({ open }) => (
                <div className='border-t border-mi-text-quaternary'>
                  <DisclosureButton className='flex w-full items-center gap-2 py-4 text-left'>
                    <CardGiftcardOutlinedIcon className='h-5 w-5' />
                    <span className='flex-1 text-base font-bold'>{t('pricing.gift.heading')}</span>
                    {open ? (
                      <ExpandLessIcon className='h-6 w-6 text-mi-text-gray' />
                    ) : (
                      <ExpandMoreIcon className='h-6 w-6 text-mi-text-gray' />
                    )}
                  </DisclosureButton>
                  <DisclosurePanel className='border-t border-mi-text-quaternary pt-4 text-base leading-[1.6]'>
                    {t('pricing.gift.body')}
                  </DisclosurePanel>
                </div>
              )}
            </Disclosure>
          )}
        </div>
      </section>

      <PremiumPlansModal
        isOpen={isPremiumPlansModalOpen}
        onClose={() => setIsPremiumPlansModalOpen(false)}
        premiumDeepLinkUrl={premiumDeepLinkUrl}
        logClickedLearnMoreAboutPremium={logClickedLearnMoreAboutPremium}
      />
    </>
  )
}
